import {
  getRoleSharesByTemplate,
  getTemplateSharesByRole,
  upsertTemplateRoleShare,
  removeTemplateRoleShare,
  updateTemplateRoleSharePermissions,
  searchSharesByTemplate,
  searchSharesByRole3,
  type ContractGenerationTemplateRoleShareInDb,
  type ContractGenerationTemplateRoleShareInDbTemplate,
  type ContractGenerationTemplateRoleShareInDbRole,
} from '@/api';
import { toAccessPermission } from '@/utils/accessPermission';

function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export interface TemplateShareSearchParams {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

export const TemplateShareService = {
  /**
   * Get all role shares for a template.
   */
  async getRoleSharesByTemplate(
    templateUid: string
  ): Promise<ContractGenerationTemplateRoleShareInDb[]> {
    try {
      const response = await getRoleSharesByTemplate({ path: { template_uid: templateUid } });
      if (!response.data) {
        throw new Error('Failed to fetch template role shares');
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to load shared roles. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get all template shares for a role (templates shared with this role).
   */
  async getTemplateSharesByRole(
    roleUid: string
  ): Promise<ContractGenerationTemplateRoleShareInDb[]> {
    try {
      const response = await getTemplateSharesByRole({ path: { role_uid: roleUid } });
      if (!response.data) {
        throw new Error('Failed to fetch templates shared with this role');
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          'Failed to load templates shared with this role. Please try again.'
        ),
        { cause: error }
      );
    }
  },

  /**
   * Search role shares for a template (paginated).
   */
  async searchRoleSharesByTemplate(
    templateUid: string,
    params: TemplateShareSearchParams = {}
  ): Promise<{ results: ContractGenerationTemplateRoleShareInDbTemplate[]; result_count: number }> {
    try {
      const pageSize = params.pageSize ?? 20;
      const page = params.page ?? 1;
      const body = {
        text: params.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: params.sortKey ?? 'created_at',
        sort_direction: params.sortDirection ?? 'desc',
        similarity_threshold: 0.5,
      };
      const response = await searchSharesByTemplate({
        path: { template_uid: templateUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to search template role shares');
      }
      return {
        results: response.data.results,
        result_count: response.data.result_count,
      };
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to search shared roles. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Search template shares for a role (paginated).
   */
  async searchTemplateSharesByRole(
    roleUid: string,
    params: TemplateShareSearchParams = {}
  ): Promise<{ results: ContractGenerationTemplateRoleShareInDbRole[]; result_count: number }> {
    try {
      const pageSize = params.pageSize ?? 20;
      const page = params.page ?? 1;
      const body = {
        text: params.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: params.sortKey ?? 'created_at',
        sort_direction: params.sortDirection ?? 'desc',
        similarity_threshold: 0.5,
      };
      const response = await searchSharesByRole3({
        path: { role_uid: roleUid },
        body,
      });
      if (!response.data) {
        throw new Error('Failed to search template shares for role');
      }
      return {
        results: response.data.results,
        result_count: response.data.result_count,
      };
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to search shared templates. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Upsert a role share for a template (create or update). Permissions is a bitmask (e.g. READ|WRITE = 3).
   */
  async upsertRoleShare(
    templateUid: string,
    roleUid: string,
    permissions: number
  ): Promise<ContractGenerationTemplateRoleShareInDb> {
    try {
      const response = await upsertTemplateRoleShare({
        path: { template_uid: templateUid, role_uid: roleUid },
        body: { permissions: toAccessPermission(permissions) },
      });
      if (!response.data) {
        throw new Error('Failed to upsert role share');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to share with role. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Update permissions for a role share. Permissions is a bitmask.
   */
  async updateRoleSharePermissions(
    templateUid: string,
    roleUid: string,
    permissions: number
  ): Promise<ContractGenerationTemplateRoleShareInDb> {
    try {
      const response = await updateTemplateRoleSharePermissions({
        path: { template_uid: templateUid, role_uid: roleUid },
        body: { permissions: toAccessPermission(permissions) },
      });
      if (!response.data) {
        throw new Error('Failed to update role share permissions');
      }
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update permissions. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Remove a role share.
   */
  async removeRoleShare(templateUid: string, roleUid: string): Promise<void> {
    try {
      await removeTemplateRoleShare({
        path: { template_uid: templateUid, role_uid: roleUid },
      });
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to remove role share. Please try again.'),
        { cause: error }
      );
    }
  },
};
